<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useDisplay } from 'vuetify'
import { useSupportStore } from '@/stores/support.store'
import { useUsersStore } from '@/stores'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import MessageBubble from '@core/components/MessageBubble.vue'
import SupportMemberContext from '@core/components/SupportMemberContext.vue'

const supportStore = useSupportStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)
const { smAndDown } = useDisplay()

const inputText = ref('')
const messagesContainer = ref(null)
const imageInput = ref(null)
const uploading = ref(false)
const searchQuery = ref('')
const showContextPanel = ref(false)
const showThread = ref(false)

const {
  adminConversations,
  adminActiveConversationId,
  adminActiveConversation,
  adminMessages,
  adminTotalUnread,
  memberContext,
  loadingAdminConversations,
  loadingAdminMessages,
  loadingMemberContext,
  sendingMessage,
} = storeToRefs(supportStore)

onMounted(() => {
  supportStore.fetchAdminConversations()
})

onUnmounted(() => {
  supportStore.clearAdminActiveConversation()
})

const filteredConversations = computed(() => {
  if (!searchQuery.value.trim()) return adminConversations.value
  const q = searchQuery.value.toLowerCase()
  
  return adminConversations.value.filter(c => {
    const m = c.member
    if (!m) return false
    const fullName = `${m.firstName || ''} ${m.lastName || ''}`.toLowerCase()
    
    return fullName.includes(q) || (m.email || '').toLowerCase().includes(q)
  })
})

async function selectConversation(conv) {
  showThread.value = true
  await supportStore.loadAdminConversation(conv._id)
  supportStore.fetchMemberContext(conv._id)
  scrollToBottom()
}

function backToInbox() {
  showThread.value = false
  supportStore.clearAdminActiveConversation()
}

async function handleSend() {
  const text = inputText.value.trim()
  if (!text && !uploading.value) return
  if (!adminActiveConversationId.value) return

  inputText.value = ''
  await supportStore.sendAdminMessage(adminActiveConversationId.value, { text })
  scrollToBottom()
}

function handleKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

async function handleImageUpload(event) {
  const file = event.target.files?.[0]
  if (!file) return
  if (!adminActiveConversationId.value) return

  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)

    const axios = (await import('@axios')).default
    const { data } = await axios.post('/media/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    if (data.imageId) {
      await supportStore.sendAdminMessage(adminActiveConversationId.value, {
        imageIds: [data.imageId],
      })
      scrollToBottom()
    }
  } catch (err) {
    console.error('Image upload failed', err)
  } finally {
    uploading.value = false
    if (imageInput.value) imageInput.value.value = ''
  }
}

function handleMarkUnread(conv) {
  supportStore.markUnreadAdmin(conv._id)
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

function formatTime(date) {
  if (!date) return ''
  const m = moment(date)
  if (m.isSame(moment(), 'day')) return m.format('h:mm A')
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
  
  return m.format('MMM D')
}

function isOwnMessage(msg) {
  return msg.senderId === myUser.value?._id
}

function showSender(msg, idx) {
  if (idx === 0) return true
  const prev = adminMessages.value[idx - 1]
  
  return prev.senderId !== msg.senderId || prev.senderType !== msg.senderType
}

function membershipBadgeColor(type) {
  switch (type) {
  case 'platinum': return 'primary-dark'
  case 'trial': return 'primary'
  case 'premium': return 'info'
  default: return 'grey'
  }
}

watch(() => adminMessages.value.length, () => {
  scrollToBottom()
})
</script>

<template>
  <div class="content-wrapper mt-6">
    <VCard class="admin-support-card">
      <div class="admin-support-layout">
        <!-- Left panel: Inbox -->
        <div
          class="inbox-panel"
          :class="{ 'inbox-panel--hidden': smAndDown && showThread }"
        >
          <!-- Inbox header -->
          <div class="inbox-header px-4">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold">RM Support Inbox</span>
              <VSpacer />
              <VBadge
                v-if="adminTotalUnread > 0"
                :content="adminTotalUnread"
                color="error"
                inline
              />
            </div>
            <VTextField
              v-model="searchQuery"
              placeholder="Search members..."
              variant="outlined"
              density="compact"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="mt-2"
              style="font-size: 16px"
            />
          </div>

          <!-- Conversation list -->
          <div class="inbox-list">
            <div
              v-if="loadingAdminConversations"
              class="d-flex justify-center pa-6"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="24"
              />
            </div>

            <div
              v-else-if="filteredConversations.length === 0"
              class="pa-6 text-center text-medium-emphasis text-body-2"
            >
              No support conversations yet
            </div>

            <template v-else>
              <div
                v-for="conv in filteredConversations"
                :key="conv._id"
                class="inbox-item"
                :class="{
                  'inbox-item--active': conv._id === adminActiveConversationId,
                  'inbox-item--unread': conv.unread > 0,
                }"
                @click="selectConversation(conv)"
                @contextmenu.prevent="handleMarkUnread(conv)"
              >
                <VAvatar
                  v-if="conv.member?.profileImageId"
                  size="40"
                  class="mr-3 flex-shrink-0"
                >
                  <VImg :src="mediaUrl(conv.member.profileImageId, 'thumb')" />
                </VAvatar>
                <VAvatar
                  v-else
                  size="40"
                  color="grey-lighten-3"
                  class="mr-3 flex-shrink-0"
                >
                  <span class="text-caption font-weight-medium">
                    {{ conv.member?.firstName?.[0] || '?' }}{{ conv.member?.lastName?.[0] || '' }}
                  </span>
                </VAvatar>

                <div class="flex-1 min-w-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 font-weight-medium text-truncate flex-1">
                      {{ conv.member?.firstName }} {{ conv.member?.lastName }}
                    </span>
                    <span class="text-caption text-medium-emphasis ml-2 flex-shrink-0">
                      {{ formatTime(conv.lastMessageAt) }}
                    </span>
                  </div>
                  <div class="d-flex align-center">
                    <VChip
                      v-if="conv.member?.membershipType"
                      :color="membershipBadgeColor(conv.member.membershipType)"
                      size="x-small"
                      variant="tonal"
                      class="mr-1"
                    >
                      {{ conv.member.membershipType }}
                    </VChip>
                    <span class="text-caption text-medium-emphasis text-truncate flex-1">
                      {{ conv.lastMessage?.text || 'No messages yet' }}
                    </span>
                    <div
                      v-if="conv.unread > 0"
                      class="unread-dot ml-2"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Right panel: Thread + context -->
        <div
          class="thread-panel"
          :class="{ 'thread-panel--hidden': smAndDown && !showThread }"
        >
          <template v-if="adminActiveConversationId">
            <div class="thread-area">
              <!-- Thread header -->
              <div class="thread-header d-flex align-center px-4">
                <VBtn
                  v-if="smAndDown"
                  icon
                  variant="text"
                  size="small"
                  class="mr-1"
                  @click="backToInbox"
                >
                  <VIcon>mdi-arrow-left</VIcon>
                </VBtn>
                <span class="text-subtitle-2 font-weight-bold flex-1 text-truncate">
                  {{ adminActiveConversation?.member?.firstName }}
                  {{ adminActiveConversation?.member?.lastName }}
                </span>
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  :color="showContextPanel ? 'primary' : 'medium-emphasis'"
                  @click="showContextPanel = !showContextPanel"
                >
                  <VIcon size="20">
                    mdi-account-details
                  </VIcon>
                </VBtn>
              </div>

              <div class="thread-content-layout">
                <!-- Messages -->
                <div class="thread-messages-wrapper">
                  <div
                    ref="messagesContainer"
                    class="thread-messages"
                  >
                    <div
                      v-if="loadingAdminMessages"
                      class="d-flex justify-center pa-6"
                    >
                      <VProgressCircular
                        indeterminate
                        color="primary"
                        size="24"
                      />
                    </div>

                    <div
                      v-else-if="adminMessages.length === 0"
                      class="pa-6 text-center text-medium-emphasis text-body-2"
                    >
                      No messages yet
                    </div>

                    <template v-else>
                      <MessageBubble
                        v-for="(msg, idx) in adminMessages"
                        :key="msg._id"
                        :message="msg"
                        :is-own="isOwnMessage(msg)"
                        :show-sender="showSender(msg, idx)"
                      />
                    </template>
                  </div>

                  <!-- Composer -->
                  <div class="thread-composer px-3 py-2">
                    <div class="d-flex align-end gap-2">
                      <VBtn
                        icon
                        variant="text"
                        size="small"
                        color="medium-emphasis"
                        :loading="uploading"
                        @click="imageInput?.click()"
                      >
                        <VIcon size="20">
                          mdi-image-plus
                        </VIcon>
                      </VBtn>
                      <input
                        ref="imageInput"
                        type="file"
                        accept="image/*"
                        style="display: none"
                        @change="handleImageUpload"
                      >
                      <VTextarea
                        v-model="inputText"
                        placeholder="Reply..."
                        variant="outlined"
                        density="compact"
                        rows="1"
                        max-rows="4"
                        auto-grow
                        hide-details
                        class="flex-1"
                        style="font-size: 16px"
                        @keydown="handleKeydown"
                      />
                      <VBtn
                        icon
                        color="primary"
                        size="small"
                        :disabled="!inputText.trim() || sendingMessage"
                        :loading="sendingMessage"
                        @click="handleSend"
                      >
                        <VIcon size="20">
                          mdi-send
                        </VIcon>
                      </VBtn>
                    </div>
                  </div>
                </div>

                <!-- Context sidebar (desktop) -->
                <div
                  v-if="showContextPanel && !smAndDown"
                  class="context-sidebar"
                >
                  <SupportMemberContext
                    :member="memberContext"
                    :loading="loadingMemberContext"
                  />
                </div>
              </div>
            </div>

            <!-- Context dialog (mobile) -->
            <VDialog
              v-if="smAndDown"
              v-model="showContextPanel"
              fullscreen
              no-click-animation
              :retain-focus="false"
              scroll-strategy="none"
            >
              <VCard>
                <VToolbar density="compact">
                  <VToolbarItems>
                    <VBtn
                      icon
                      variant="text"
                      @click="showContextPanel = false"
                    >
                      <VIcon>mdi-chevron-left</VIcon>
                    </VBtn>
                  </VToolbarItems>
                  <VSpacer />
                  <span class="text-subtitle-2 font-weight-bold">
                    {{ adminActiveConversation?.member?.firstName }}
                    {{ adminActiveConversation?.member?.lastName }}
                  </span>
                  <VSpacer />
                  <VToolbarItems style="width: 48px" />
                </VToolbar>
                <div class="overflow-y-auto">
                  <SupportMemberContext
                    :member="memberContext"
                    :loading="loadingMemberContext"
                  />
                </div>
              </VCard>
            </VDialog>
          </template>

          <div
            v-else
            class="d-flex align-center justify-center h-100 text-medium-emphasis"
          >
            Select a conversation
          </div>
        </div>
      </div>
    </VCard>
  </div>
</template>

<style scoped>
.admin-support-card {
  height: calc(100vh - 120px);
  max-height: 900px;
  overflow: hidden;
}

.admin-support-layout {
  display: flex;
  height: 100%;
}

/* Inbox panel */
.inbox-panel {
  width: 320px;
  min-width: 320px;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
}

.inbox-header {
  padding-top: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.inbox-list {
  flex: 1;
  overflow-y: auto;
}

.inbox-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
}

.inbox-item:hover {
  background: var(--ds-surface-alt, #f8f8f9);
}

.inbox-item--active {
  background: rgba(63, 153, 143, 0.08);
}

.inbox-item--unread .text-body-2 {
  font-weight: 700;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ds-brand-primary-dark, #3F998F);
  flex-shrink: 0;
}

/* Thread panel */
.thread-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.thread-area {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.thread-header {
  height: 48px;
  min-height: 48px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.thread-content-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

.thread-messages-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.thread-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.thread-composer {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: var(--ds-surface-card);
}

/* Context sidebar */
.context-sidebar {
  width: 280px;
  min-width: 280px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  overflow-y: auto;
}

/* Mobile responsive */
@media (max-width: 959px) {
  .inbox-panel {
    width: 100%;
    min-width: 100%;
  }

  .inbox-panel--hidden {
    display: none;
  }

  .thread-panel--hidden {
    display: none;
  }

  .thread-panel {
    width: 100%;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
